import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ApproveRejectActions } from 'modules/shared/components';
import { InlineApproveSection, InlineRejectSection, ScoredInterviewRound } from '../components';
import { INTERVIEW_ACTION } from '../interview.constants';

const ROUND_2_CRITERIA = Object.freeze([
  { key: 'technicalKnowledge', label: 'Technical knowledge', max: 20 },
  { key: 'teamPlayer', label: 'Team player', max: 20 },
  { key: 'problemSolvingSkills', label: 'Problem solving skills', max: 20 },
  { key: 'peopleSkills', label: 'People skills', max: 20 },
]);

const CURRENT_ROUND_KEY = 'round2';

/**
 * InterviewRound2
 *
 * Controls evaluation and decision flow for the second interview round (manager round).
 *
 * Responsibilities:
 * - Render scored evaluation criteria for Round 2
 * - Calculate and display total score
 * - Gate approve / reject actions behind validation
 * - Emit structured decision payloads to parent handlers
 *
 * Props:
 * @param {Object} data - Existing round data (scores) when revisiting the round.
 * @param {boolean} readOnly - Disables actions and editing when true.
 * @param {Function} onApprove - Callback invoked with approval payload.
 * @param {Function} onReject - Callback invoked with rejection payload.
 *
 * Notes:
 * - Uses react-hook-form internally for round-scoped state only.
 * - Action mode is local and resets on cancel.
 */
export default function InterviewRound2({ data, readOnly, onApprove, onReject }) {
  const [actionMode, setActionMode] = useState<string>(INTERVIEW_ACTION.IDLE);

  const methods = useForm({
    defaultValues: {
      round2: data ?? {},
      rejectionReason: '',
      nextInterview: null,
    },
  });
  const { watch, trigger, resetField } = methods;

  const scores = watch('round2');
  const rejectionReason = watch('rejectionReason');
  const nextInterview = watch('nextInterview');

  const totalScore = ROUND_2_CRITERIA.reduce((acc, item) => {
    const value = Number(scores?.[item.key]?.score);
    return acc + (Number.isFinite(value) ? value : 0);
  }, 0);

  const resetActionState = () => {
    setActionMode(INTERVIEW_ACTION.IDLE);
    resetField('rejectionReason');
  };

  const handleApprove = async () => {
    const isFormValid = await trigger(['round2', 'nextInterview'], { shouldFocus: true });
    if (!isFormValid) return;
    onApprove?.({
      round2: scores,
      nextInterview,
      action: INTERVIEW_ACTION.APPROVE,
      currentRoundKey: CURRENT_ROUND_KEY,
    });
  };

  const handleReject = async () => {
    const isFormValid = await trigger(['rejectionReason', 'round2'], { shouldFocus: true });
    if (!isFormValid) return;
    onReject?.({ round2: scores, rejectionReason, action: INTERVIEW_ACTION.REJECT });
  };

  return (
    <FormProvider {...methods}>
      <form className='column gap-4'>
        <ScoredInterviewRound
          title='ROUND 2 - MANAGER'
          roundKey='round2'
          criteria={ROUND_2_CRITERIA}
          readOnly={readOnly}
          totalScore={totalScore}
        />
        {!readOnly && actionMode === INTERVIEW_ACTION.IDLE && (
          <ApproveRejectActions
            onApprove={async () => {
              const isValid = await trigger();
              if (!isValid) return;
              setActionMode(INTERVIEW_ACTION.APPROVE);
            }}
            onReject={async () => {
              const isValid = await trigger();
              if (!isValid) return;
              setActionMode(INTERVIEW_ACTION.REJECT);
            }}
          />
        )}
        {actionMode === INTERVIEW_ACTION.REJECT && (
          <InlineRejectSection
            name='rejectionReason'
            onCancel={resetActionState}
            onConfirm={handleReject}
            disabled={readOnly}
          />
        )}
        {actionMode === INTERVIEW_ACTION.APPROVE && (
          <InlineApproveSection
            name='nextInterview'
            onCancel={resetActionState}
            onConfirm={handleApprove}
            disabled={readOnly}
          />
        )}
      </form>
    </FormProvider>
  );
}
